import React, { useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ICustomMenu from "models/CustomMenu";
import { StyledMenu } from "./StyledMenu";
import { useGetUsers } from "hooks/queries/useGetUsers";
import { useDeleteUserMutation } from "hooks/mutations/useDeleteUserMutation";
import { useSnackbar } from "hooks/useSnackbar";
import useEditUserMutation from "hooks/mutations/useEditUserMutation";
import { useForm, Controller } from "react-hook-form";
import { IUser } from "models/Employee";

export const EmployeeCardActionButton = ({
  anchorEl,
  open,
  handleClose,
  userData: { id, first_name, last_name, user_matricule, email, phone, profile_image },
  setUserData,
}: ICustomMenu) => {
  const theme = useTheme();
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const { showSnackbar, getSnackbarProps } = useSnackbar();
  const { refetch } = useGetUsers();
  const { mutate: editUser } = useEditUserMutation({
    callback: () => refetch(),
    id: id,
    showSnackbar,
  });

  const { mutate: deleteUser } = useDeleteUserMutation({
    callback: () => refetch(),
    id: id,
    showSnackbar,
  });

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { // set default values for the form fields
      first_name,
      last_name,
      user_matricule,
      email,
      phone,
    },
  });

  const openEditDialog = () => {
    setOpenDialog(true);
    handleClose();
  };

  const closeDialog = () => {
    setOpenDialog(false);
    reset();
  };

  const openDeleteConfirmationDialog = () => {
    setDeleteConfirmationOpen(true);
    handleClose();
  };

  const closeDeleteConfirmationDialog = () => {
    setDeleteConfirmationOpen(false);
  };

  const handleDelete = () => {
    deleteUser(id);
    refetch();
    closeDeleteConfirmationDialog();
  };

  const handleEditUser = (data: IUser) => {
    const payload = {
      first_name: data.first_name,
      last_name: data.last_name,
      user_matricule: data.user_matricule,
      // email: data.email,
      phone: data.phone,
    };
    editUser({ payload });
    closeDialog();
  };

  return (
    <>
      <StyledMenu
        id="demo-customized-menu"
        MenuListProps={{ "aria-labelledby": "demo-customized-menu" }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem onClick={openEditDialog} disableRipple>
          <EditIcon />
          Edit
        </MenuItem>
        <MenuItem onClick={openDeleteConfirmationDialog} disableRipple>
          <DeleteIcon />
          Delete
        </MenuItem>
      </StyledMenu>

      {/* Edit Dialog here */}

      <Dialog open={openDialog} onClose={closeDialog} PaperProps={{ style: { width: "80%" } }}>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "center",
            color: theme.palette.primary.main,
            fontWeight: 800,
          }}
        >
          Edit Info
        </DialogTitle>
        <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          <Avatar
            src={profile_image}
            alt={`${first_name} ${last_name}`}
            sx={{ width: 120, height: 120, border: "3px solid lightgrey" }}
          />
          <Typography
            variant="subtitle2"
            sx={{ color: theme.palette.primary.main, cursor: "pointer", mt: 1 }}
          >
            Change the Profile Image
          </Typography>
        </Box>

        <DialogContent>
          <Typography
            variant="subtitle2"
            sx={{ color: theme.palette.primary.main, fontWeight: 800 }}
          >
            Firstname
          </Typography>
          <Controller
            name="first_name"
            control={control}
            render={({ field }) => <TextField {...field} sx={{ mb: 1 }} fullWidth />}
          />
          <Typography
            variant="subtitle2"
            sx={{ color: theme.palette.primary.main, fontWeight: 800 }}
          >
            Lastname
          </Typography>
          <Controller
            name="last_name"
            control={control}
            render={({ field }) => <TextField {...field} fullWidth sx={{ mb: 1 }} />}
          />
          <Typography
            variant="subtitle2"
            sx={{ color: theme.palette.primary.main, fontWeight: 800 }}
          >
            Username
          </Typography>
          <Controller
            name="user_matricule"
            control={control}
            render={({ field }) => <TextField {...field} fullWidth sx={{ mb: 1 }} />}
          />
          <Typography
            variant="subtitle2"
            sx={{ color: theme.palette.primary.main, fontWeight: 800 }}
          >
            Company Email
          </Typography>
          <Controller
            name="email"
            control={control}
            render={({ field }) => <TextField {...field} fullWidth sx={{ mb: 1 }} />}
          />
          <Typography
            variant="subtitle2"
            sx={{ color: theme.palette.primary.main, fontWeight: 800 }}
          >
            Phone
          </Typography>
          <Controller
            name="phone"
            control={control}
            render={({ field }) => <TextField {...field} fullWidth sx={{ mb: 1 }} />}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="error" onClick={closeDialog}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit(handleEditUser)}>
            UPDATE
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog here */}

      <Dialog open={deleteConfirmationOpen} onClose={closeDeleteConfirmationDialog}>
        <DialogTitle style={{ backgroundColor: "#f44336", color: "white" }}>
          <Typography variant="h6" color="inherit" fontWeight={800}>
            Confirmation
          </Typography>
        </DialogTitle>
        <DialogContent style={{ padding: "16px 32px" }}>
          <Typography variant="body1">Are you sure you want to Delete this User ?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirmationDialog} color="error">
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        {...getSnackbarProps()}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={4000}
      >
        <Alert {...getSnackbarProps()}>{getSnackbarProps().message}</Alert>
      </Snackbar>
    </>
  );
};
